import React from "react";
import {
  Collapse,
  Typography,
  ListItem,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useMsal } from "@azure/msal-react";
import {
  Bars4Icon,
  GlobeAmericasIcon,
  PhoneIcon,
  RectangleGroupIcon,
  ArrowUturnRightIcon,
  UserIcon,
  CreditCardIcon,
  BuildingOffice2Icon,
  LightBulbIcon,
  ClipboardDocumentListIcon,
  InboxIcon,
} from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { logoutAzure } from "../authConfig";
import { getSubscriptionDetails } from "../api/subscriptionService";
import { useRoleNavigation } from "../hooks/useRoleNavigation";
import { roleDestination } from "../utils/roleNavigation";
function NavListMenu({ mobile = false, onNavigate, unreadMessages = 0, canManageCredits = false, canUseProspects = false }) {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const {
    isLoggedIn,
    authReady,
    token,
    profile,
    user_type,
    subscription_id,
    isApproverLoggedIn,
    activeWorkspace,
    setActiveWorkspace,
    assignedRoles = [],
    activeRole,
    setActiveRole = () => {},
    setIsApproverLoggedIn,
    setUser_type,
    setIsLoggedIn,
    refreshSessionRoles,
    logout,
  } = useAuth();
  const switchRole = useRoleNavigation();
  const isAdmin = user_type === "admin";
  const selectableRoles = (Array.isArray(assignedRoles) ? assignedRoles : []).filter((role) => roleDestination(role));
  const selectRole = (role) => {
    switchRole(role);
    setIsMenuOpen(false);
    setIsMobileMenuOpen(false);
    if (mobile) onNavigate?.();
  };
  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsMobileMenuOpen(false);
    if (mobile) onNavigate?.();
  };
  const roleChoices = (
    <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800" onClick={(event) => event.stopPropagation()}>
      <p className="text-sm font-semibold text-gray-900 dark:text-white">Current role: {activeRole || (isAdmin ? "Organization Owner" : "Select a role")}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {isAdmin && <button type="button" onClick={() => selectRole("Organization Owner")} aria-pressed={!activeRole} className={`rounded-full border px-3 py-1 text-sm ${!activeRole ? "border-purple-700 bg-purple-700 text-white" : "border-gray-300 text-gray-900 dark:text-white"}`}>Organization Owner</button>}
        {selectableRoles.map((role) => <button key={role} type="button" onClick={() => selectRole(role)} aria-pressed={activeRole === role} className={`rounded-full border px-3 py-1 text-sm ${activeRole === role ? "border-purple-700 bg-purple-700 text-white" : "border-gray-300 text-gray-900 dark:text-white"}`}>{role}</button>)}
        {!isAdmin && selectableRoles.length === 0 && <span className="text-sm text-gray-600 dark:text-gray-300">No assigned roles</span>}
      </div>
      {!isAdmin && selectableRoles.length === 1 && <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">Only this role is assigned to the current sign-in. Additional roles appear here after they are saved for this user.</p>}
    </div>
  );
  const { instance } = useMsal();
  const canAccessApprover =
    isApproverLoggedIn || ["approver", "CMO/CXO"].includes(user_type);
  const isInApproverWorkspace = activeWorkspace === "approver";
  const [subDetails, setSubDetails] = React.useState(null);
  React.useEffect(() => {
    if (!isLoggedIn || !(isMenuOpen || isMobileMenuOpen) || !refreshSessionRoles) return;
    refreshSessionRoles().catch((error) => console.warn("Unable to refresh assigned roles", error));
  }, [isLoggedIn, isMenuOpen, isMobileMenuOpen, refreshSessionRoles]);
  React.useEffect(() => {
    setSubDetails(null);
    if (!isAdmin || !authReady || !isLoggedIn || !subscription_id || !(isMenuOpen || isMobileMenuOpen)) return;
    const controller = new AbortController();
    let cancelled = false;
    getSubscriptionDetails(subscription_id, { token, signal: controller.signal })
      .then((data) => {
        if (!cancelled) setSubDetails(data);
      })
      .catch(() => {
        if (!cancelled) setSubDetails(null);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isAdmin, authReady, isLoggedIn, subscription_id, token, isMenuOpen, isMobileMenuOpen]);
  const navListMenuItems = [
    ...(isLoggedIn && subscription_id && canUseProspects
      ? [
          {
            title: "AI Prospect Hunter",
            description: "Research and prioritize prospects",
            icon: GlobeAmericasIcon,
            link: "/prospect-hunter",
          },
          {
            title: "AI Demo Booker",
            description: "Qualify leads and schedule demos",
            icon: ClipboardDocumentListIcon,
            link: "/demo-booker",
          },
        ]
      : []),
    ...(isAdmin && isLoggedIn && subscription_id && canManageCredits
      ? [
          {
            title: "Credit Catalog",
            description: "Price your products and services",
            icon: RectangleGroupIcon,
            link: "/credit-catalog",
          },
          {
            title: "Promo Codes",
            description: "Create and manage credit offers",
            icon: CreditCardIcon,
            link: "/promo-campaigns",
          },
        ]
      : []),
    ...(isAdmin && isLoggedIn && subscription_id
      ? [
          {
            title: "Manage Subscription",
            description: "View plan, billing and payments",
            icon: CreditCardIcon,
            link: "/admin/upgrade",
          },
          {
            title: "Industry Intelligence",
            description: "News, company insights & competitor intel",
            icon: LightBulbIcon,
            link: "/industry-intelligence",
          },
        ]
      : []),
    ...(isLoggedIn && subscription_id ? [{ title: "Credit Wallet", description: "Redeem credits and order services", icon: CreditCardIcon, link: "/credits" }] : []),
    ...(isLoggedIn && subscription_id ? [{
      title: unreadMessages ? `Inbox (${unreadMessages})` : "Inbox",
      description: "Read organization messages",
      icon: InboxIcon,
      link: "/marketingintegrations?tab=inbox",
    }] : []),
    ...(isAdmin && isLoggedIn
      ? [
          {
            title: "Knowledge Library",
            description: "Browse and manage the organization knowledge base",
            icon: LightBulbIcon,
            link: "/knowledgelibrary",
          },
          {
            title: "Activities",
            description: "Create and track organization activities",
            icon: ClipboardDocumentListIcon,
            link: "/activities",
          },
        ]
      : []),
    ...(isAdmin ? [{
      title: "Edit Organization",
      description: "Update organization details",
      icon: BuildingOffice2Icon,
      link: "/home/organization/details",
    },
    {
      title: "Add User",
      description: "Add user into organization",
      icon: UserIcon,
      link: "/adduser",
    }] : []),
    ...(isAdmin && canAccessApprover
      ? [
          {
            title: isInApproverWorkspace
              ? "Switch to Admin Menu"
              : "Approver Dashboard",
            description: isInApproverWorkspace
              ? "Return to the organization admin workspace"
              : "Approve BUs, offerings, assets, and campaigns",
            icon: GlobeAmericasIcon,
            link: isInApproverWorkspace ? "/home/organization" : "/dashboard",
            workspace: isInApproverWorkspace ? "admin" : "approver",
          },
        ]
      : []),
    {
      title: "Blog",
      description: "Find the perfect solution for your needs.",
      icon: Bars4Icon,
      externalLink: "https://www.stunm.in/stunm-blogs",
    },
    {
      title: "Contact",
      description: "Find the perfect solution for your needs.",
      icon: PhoneIcon,
      link: "/home/organization/contact",
    },
    ...(isAdmin ? [{
      title: "Products",
      description: "Find the perfect solution for your needs.",
      icon: RectangleGroupIcon,
      link: "/admin/upgrade",
    }] : []),
    {
      title:
        user_type === "admin"
          ? "Log out (Org Password Login)"
          : "Log out (User Login)",
      description: "Log out and login soon.",
      icon: ArrowUturnRightIcon,
      link: "/orgpasswordlogin",
      logout: true,
    },
  ];

  const renderItems = navListMenuItems.map(
    ({ icon, title, description, link, externalLink, workspace, logout: isLogout }, key) => (
      <MenuItem
        key={key}
        aria-label={title}
        className="flex items-center gap-3 rounded-lg cursor-pointer dark:hover:bg-gray-700"
        onClick={async () => {
          closeMenu();
          if (externalLink) {
            window.open(externalLink, "_blank", "noopener,noreferrer");
          } else if (link) {
            if (isLogout) {
              // Await the bounded app logout so its HttpOnly refresh cookie is
              // revoked before either navigation or the identity-provider flow.
              await logout();
              await logoutAzure(instance, setIsLoggedIn, setIsApproverLoggedIn, setUser_type);
              return;
            }
            if (workspace) {
              setActiveRole(workspace === "approver" && assignedRoles.includes("CMO/CXO") ? "CMO/CXO" : "");
              setActiveWorkspace(workspace);
            }
            navigate(link);
          }
        }}
      >
        <div className="flex items-center justify-center rounded-lg !bg-blue-gray-50 dark:!bg-gray-700 p-2">
          {React.createElement(icon, {
            strokeWidth: 2,
            className: "h-6 text-gray-900 dark:text-white w-6",
          })}
        </div>
        <div>
          <Typography
            variant="h6"
            color="blue-gray"
            className="flex items-center text-sm font-bold dark:text-white"
          >
            {title}
          </Typography>
          <Typography
            variant="paragraph"
            className="text-xs !font-medium text-blue-gray-500 dark:text-gray-400"
          >
            {description}
          </Typography>
        </div>
      </MenuItem>
    )
  );
  return (
    <React.Fragment>
      {mobile ? (
        <div className="w-full">
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            aria-expanded={isMobileMenuOpen}
            aria-controls="organization-mobile-options"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
          >
            {profile?.orgname || "Organization"}
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${isMobileMenuOpen ? "rotate-180" : ""}`} />
          </button>
          <Collapse open={isMobileMenuOpen}>
            <div id="organization-mobile-options" className="rounded-lg bg-white p-2 dark:bg-gray-800">
              {roleChoices}
              {renderItems}
            </div>
          </Collapse>
        </div>
      ) : (
      <Menu
        open={isMenuOpen}
        handler={setIsMenuOpen}
        offset={{ mainAxis: 20 }}
        placement="bottom"
      >
        <MenuHandler>
          <Typography as="div" variant="small" className="font-medium">
            <ListItem
              className="flex items-center gap-2 py-2 pr-4 font-medium text-gray-900 dark:text-white"
              selected={isMenuOpen}
            >
              {/* Resources */}
              {isLoggedIn ? (
                <>
                  <div className="flex items-center justify-between text-blue-gray-900 dark:text-white">
                    <Typography
                      href="/home/organization"
                      className="mr-4 cursor-pointer py-1.5 font-medium font-bold text-white"
                    >
                      {profile?.orgname || "Organization"}
                    </Typography>
                  </div>
                </>
              ) : (
                <span>Log in</span>
              )}
              <ChevronDownIcon
                strokeWidth={2.5}
                className={`h-3 w-3 transition-transform ${
                  isMenuOpen ? "rotate-180" : ""
                }`}
              />
            </ListItem>
          </Typography>
        </MenuHandler>
        <MenuList className="grid max-w-screen-xl grid-cols-3 gap-y-2 rounded-xl dark:bg-gray-800 dark:border-gray-700">
          <div className="col-span-3">{roleChoices}</div>
          {renderItems}
          {isLoggedIn && subscription_id && subDetails && (
            <div className="col-span-3 mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Typography
                    variant="h6"
                    className="flex items-center text-sm font-bold text-gray-900 dark:text-white"
                  >
                    {subDetails.plan || (subDetails.is_subscribed ? "Paid Plan" : "Free Plan")}
                  </Typography>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      subDetails.is_subscribed
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                        : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {subDetails.is_subscribed
                      ? (subDetails.status || "Active")?.toUpperCase()
                      : "FREE"}
                  </span>
                </div>
                <Typography
                  variant="small"
                  className="cursor-pointer text-xs font-semibold text-lightPurple dark:text-purple-300"
                  onClick={() => { closeMenu(); navigate("/admin/upgrade"); }}
                >
                  Manage
                </Typography>
              </div>
              {subDetails.is_subscribed && (
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                  <Typography className="text-xs text-gray-600 dark:text-gray-400">
                    Last paid:{" "}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {subDetails.amount || "-"}
                    </span>
                  </Typography>
                  <Typography className="text-xs text-gray-600 dark:text-gray-400">
                    Next billing:{" "}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {subDetails.current_period_end
                        ? new Date(
                            subDetails.current_period_end
                          ).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "-"}
                    </span>
                  </Typography>
                  <Typography className="text-xs text-gray-600 dark:text-gray-400">
                    Subscription ID:{" "}
                    <span className="font-mono font-semibold text-gray-900 dark:text-white">
                      {subDetails.subscription_id || "-"}
                    </span>
                  </Typography>
                  <Typography className="text-xs text-gray-600 dark:text-gray-400">
                    Payment ID:{" "}
                    <span className="font-mono font-semibold text-gray-900 dark:text-white">
                      {subDetails.payment_id || "-"}
                    </span>
                  </Typography>
                </div>
              )}
            </div>
          )}
        </MenuList>
      </Menu>
      )}
    </React.Fragment>
  );
}

export function MegaMenuWithHover({ mobile = false, onNavigate, unreadMessages = 0, canManageCredits = false, canUseProspects = false }) {
  return <NavListMenu mobile={mobile} onNavigate={onNavigate} unreadMessages={unreadMessages} canManageCredits={canManageCredits} canUseProspects={canUseProspects} />;
}
